import postcss from 'postcss';
import { LAYOUT_CLASS_NAMES } from '@lumen5/luminary';

import { parseDesignTokens, tokenPx } from './designSystemTokens';
import type { LintRule } from './types';

/**
 * Rules for the layout vocabulary the host serves every composition (a safelisted, layout-only
 * Tailwind build injected as `<style id="l5-layout">`).
 *
 * They exist because of what a utility class does when it is wrong: nothing. A coordinate that is
 * 200px off renders visibly off; a class the stylesheet does not define matches no rule and the
 * frame simply lays out as though it were never written. These rules make that silence loud.
 */

/**
 * The vocabulary, read once on first use rather than at import.
 *
 * This module is reachable from the server-rendered marketing pages, whose bundle links luminary's
 * SSR build — which does not carry this export. Touching it at module scope threw while the module
 * was still evaluating and took the whole page down with a 500. A lint rule has no business doing
 * that, so an absent vocabulary means there is nothing to check rather than everything to reject.
 */
let vocabulary: { classes: Set<string>; prefixes: Set<string> } | null = null;

function layoutVocabulary(): { classes: Set<string>; prefixes: Set<string> } {
    if (!vocabulary) {
        const names: readonly string[] = Array.isArray(LAYOUT_CLASS_NAMES)
            ? LAYOUT_CLASS_NAMES
            : [];
        vocabulary = {
            classes: new Set(names),
            prefixes: new Set(names.map((name) => name.split('-')[0]!)),
        };
    }
    return vocabulary;
}

function styleBlocks(html: string): string[] {
    const blocks: string[] = [];
    const pattern = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(html)) !== null) {
        blocks.push(match[1] ?? '');
    }
    return blocks;
}

function scriptBlocks(html: string): string[] {
    const blocks: string[] = [];
    const pattern = /<script\b[^>]*>([\s\S]*?)<\/script>/gi;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(html)) !== null) {
        blocks.push(match[1] ?? '');
    }
    return blocks;
}

/** Every class name the composition's own `<style>` blocks write a rule for. */
function selfDefinedClasses(html: string): Set<string> {
    const defined = new Set<string>();
    styleBlocks(html).forEach((css) => {
        let root;
        try {
            root = postcss.parse(css);
        } catch {
            // Unparseable CSS is `valid-css`'s finding to report, not this rule's.
            return;
        }
        root.walkRules((rule) => {
            const names = rule.selector.match(/\.(-?[_a-zA-Z][\w-]*)/g) ?? [];
            names.forEach((name) => defined.add(name.slice(1)));
        });
    });
    return defined;
}

/** Class tokens appearing in a literal `class="..."` attribute anywhere in the document. */
function authoredClassTokens(html: string): Map<string, number> {
    const counts = new Map<string, number>();
    const pattern = /\bclass\s*=\s*(?:"([^"]*)"|'([^']*)')/gi;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(html)) !== null) {
        const value = match[1] ?? match[2] ?? '';
        // A value built at runtime isn't a literal class list; whatever it resolves to is beyond
        // what source inspection can judge.
        if (/[${}+]|\{\{/.test(value)) {
            continue;
        }
        value
            .split(/\s+/)
            .filter(Boolean)
            .forEach((token) => counts.set(token, (counts.get(token) ?? 0) + 1));
    }
    return counts;
}

/**
 * Whether a class is named anywhere other than the `class` attributes that apply it — a CSS rule,
 * or a GSAP selector string in a `<script>`. A class used purely as a tween target (`.item` on a
 * row of list items) is legitimate and carries no CSS rule, so a name the composition itself
 * refers to is taken as deliberate.
 */
function namedElsewhere(html: string, className: string): boolean {
    const escaped = className.replace(/[.*+?^${}()|[\]\\/-]/g, '\\$&');
    const reference = new RegExp(`\\.${escaped}(?![\\w-])`);
    return [...styleBlocks(html), ...scriptBlocks(html)].some((block) => reference.test(block));
}

/**
 * Namespaces Tailwind has and this build deliberately drops. Writing one is a reasonable mistake
 * and a silent one, so each is named back with the reason it is missing rather than just rejected.
 */
const EXCLUDED_NAMESPACES: Record<string, string> = {
    translate: 'GSAP owns transform',
    scale: 'GSAP owns transform',
    rotate: 'GSAP owns transform',
    skew: 'GSAP owns transform',
    origin: 'GSAP owns transform',
    opacity: 'opacity is a tween channel, not a layout one',
};

const VALUE_SEGMENT =
    /^(?:\d+(?:\.\d+)?(?:\/\d+)?|\d*\.?\d*xl|xs|sm|md|lg|prose|px|auto|full|none|min|max|fit|screen|reverse|dense)$/;

/**
 * Whether a class is aimed at the layout vocabulary — an arbitrary value, a variant prefix, a
 * dropped namespace, or a real prefix carrying a value off the scale (`p-13`). A name that simply
 * is not a utility (`feed-item`, `digit-window`) is an author's own marker and none of this rule's
 * business, even when nothing styles it.
 */
function aimsAtTheVocabulary(token: string): boolean {
    if (token.includes('[') || token.includes(':')) {
        return true;
    }
    // A leading hyphen is a negative value, not part of the namespace — `-translate-x-1/2` is the
    // standard centring idiom, and reading it as namespace `''` let every negative transform
    // utility through the one check meant to stop them.
    const segments = token.replace(/^-/, '').split('-');
    const prefix = segments[0] ?? '';
    if (prefix in EXCLUDED_NAMESPACES) {
        return true;
    }
    // Only the last segment carries the value; the ones between name the axis or the property
    // (`grid-cols-14`, `gap-x-5.5`). Requiring every segment to be a value meant no multi-word
    // utility could ever be read as off-scale.
    const value = segments[segments.length - 1] ?? '';
    return (
        segments.length > 1 &&
        layoutVocabulary().prefixes.has(prefix) &&
        segments.slice(1, -1).every((segment) => /^[a-z]+$/.test(segment)) &&
        VALUE_SEGMENT.test(value)
    );
}

const unknownLayoutClass: LintRule = {
    id: 'unknown-layout-class',
    severity: 'error',
    guidance:
        'Lay content out with the layout utility classes the host injects — a safelisted Tailwind ' +
        'build covering display, flex, grid, position, inset, sizing, spacing, gap, alignment, ' +
        'order, overflow and object-fit. Only the plain class names exist: there are no ' +
        'breakpoint prefixes (`md:`, `2xl:`), no arbitrary values (`w-[847px]`, `p-[96px]`), and ' +
        'no transform utilities (`translate-x-*`, `scale-*`, `rotate-*`) because GSAP owns ' +
        'transform. A class outside that set matches no rule and silently does nothing, so write ' +
        'the nearest scale value instead (`w-full`, `p-24`, `gap-16`) or give the element your ' +
        'own CSS rule.',
    check(html: string): string[] {
        const known = layoutVocabulary().classes;
        if (!known.size) {
            return [];
        }
        const defined = selfDefinedClasses(html);
        const unknown = [...authoredClassTokens(html).keys()].filter(
            (token) =>
                !known.has(token) &&
                aimsAtTheVocabulary(token) &&
                !defined.has(token) &&
                !namedElsewhere(html, token),
        );
        if (!unknown.length) {
            return [];
        }
        const arbitrary = unknown.filter((token) => token.includes('['));
        const prefixed = unknown.filter((token) => token.includes(':'));
        const dropped = unknown.filter(
            (token) => token.replace(/^-/, '').split('-')[0]! in EXCLUDED_NAMESPACES,
        );
        const messages = [
            `${unknown.length} class ${unknown.length === 1 ? 'name is' : 'names are'} neither in ` +
                `the injected layout stylesheet nor defined by this composition's own CSS, so ` +
                `${unknown.length === 1 ? 'it matches' : 'they match'} no rule and ` +
                `${unknown.length === 1 ? 'does' : 'do'} nothing: ` +
                `${unknown.slice(0, 12).join(', ')}${unknown.length > 12 ? ', …' : ''}.`,
        ];
        if (arbitrary.length) {
            messages.push(
                `Arbitrary values (${arbitrary.slice(0, 4).join(', ')}) need a build step the ` +
                    'composition does not get. Use the nearest value on the spacing/sizing scale, ' +
                    'which runs to 96 (`p-24` is 96px, `gap-16` is 64px, `w-96` is 384px).',
            );
        }
        if (prefixed.length) {
            messages.push(
                `Variant prefixes (${prefixed.slice(0, 4).join(', ')}) are deliberately absent: a ` +
                    'composition renders at several aspect ratios and a breakpoint-keyed class ' +
                    'would quietly mean something different on each. Write the unprefixed class.',
            );
        }
        dropped.forEach((token) => {
            const reason = EXCLUDED_NAMESPACES[token.replace(/^-/, '').split('-')[0]!];
            messages.push(`\`${token}\` is not in the stylesheet on purpose: ${reason}.`);
        });
        return messages;
    },
};

const OFFSET_PROPERTIES = ['top', 'right', 'bottom', 'left', 'inset'];

/** Every authored offset on these declarations that places the element away from its edge. */
function nonZeroOffsets(declarations: string): string[] {
    return OFFSET_PROPERTIES.flatMap((property) => {
        const match = new RegExp(`(?:^|[;{\\s])${property}\\s*:\\s*([^;}]+)`, 'i').exec(
            declarations,
        );
        const value = match?.[1]?.trim();
        if (!value || /\b(auto|inherit|initial|unset|revert)\b/i.test(value)) {
            return [];
        }
        const placesAwayFromEdge =
            /-?(?:\d*\.)?\d+\s*(px|%|r?em|v[wh])/i.test(value) && !/^0(\D|$)/.test(value);
        return placesAwayFromEdge ? [`${property}: ${value}`] : [];
    });
}

function isAbsolutelyPositioned(position: string): boolean {
    return /^(absolute|fixed)$/i.test(position);
}

function* ancestorsOf(element: Element): Generator<Element> {
    let current = element.parentElement;
    while (current && current.tagName.toLowerCase() !== 'body') {
        yield current;
        current = current.parentElement;
    }
}

const POSITIONED = /^(absolute|fixed|relative|sticky)$/i;

/** The largest a canvas gets on each axis: 1920x1080 landscape, 1080x1920 vertical. */
const CANVAS_WIDEST = 1920;
const CANVAS_TALLEST = 1080;

/**
 * The declarations landing on an element: its inline `style`, plus any rule in the composition's
 * own CSS whose selector is a bare `#id`, `.class` or tag name. Compound and descendant selectors
 * are skipped rather than approximated — reading them wrong would block a correct composition.
 */
function declarationsFor(element: Element, ownRules: { selector: string; body: string }[]): string {
    const matching = ownRules
        .filter(({ selector }) => {
            try {
                return /^[#.]?[\w-]+$/.test(selector) && element.matches(selector);
            } catch {
                return false;
            }
        })
        .map(({ body }) => body);
    return [...matching, element.getAttribute('style') ?? ''].join('; ');
}

function positionOf(element: Element, declarations: string): string {
    // The layout vocabulary carries its own positioning, and it is the idiom the frame root uses.
    for (const name of ['absolute', 'fixed', 'relative', 'sticky', 'static']) {
        if (element.classList.contains(name)) {
            return name;
        }
    }
    const declared = /(?:^|[;{\s])position\s*:\s*([a-z]+)/i.exec(declarations)?.[1];
    return declared?.toLowerCase() ?? 'static';
}

function declaresZero(declarations: string, property: string): boolean {
    const value = new RegExp(`(?:^|[;{\\s])${property}\\s*:\\s*([^;}]+)`, 'i')
        .exec(declarations)?.[1]
        ?.trim();
    return value !== undefined && /^0(\D|$)/.test(value);
}

/** Whether this element covers the whole canvas rather than carving a region out of it. */
function fillsTheFrame(element: Element, declarations: string): boolean {
    if (element.classList.contains('inset-0')) {
        return true;
    }
    if (element.classList.contains('inset-x-0') && element.classList.contains('inset-y-0')) {
        return true;
    }
    if (declaresZero(declarations, 'inset')) {
        return true;
    }
    if (['top', 'right', 'bottom', 'left'].every((side) => declaresZero(declarations, side))) {
        return true;
    }

    // A box that declares itself as big as the canvas is claiming to be the canvas, so a
    // coordinate inside it is a frame coordinate. A world deliberately larger than the frame — the
    // strip a pan travels over — says so with `data-l5-allow-offscreen`, and stays a stage: its
    // contents are composed against each other, not against the frame.
    if (element.hasAttribute('data-l5-allow-offscreen')) {
        return false;
    }
    const declaredPx = (axis: string): number => {
        const value = new RegExp(`(?:^|[;{\\s])${axis}\\s*:\\s*([^;}]+)`, 'i')
            .exec(declarations)?.[1]
            ?.trim();
        return Number(/^(\d+)px$/.exec(value ?? '')?.[1] ?? 0);
    };
    return declaredPx('width') >= CANVAS_WIDEST || declaredPx('height') >= CANVAS_TALLEST;
}

/**
 * Whether this ancestor is a stage — a positioned region genuinely smaller than the canvas, which
 * its children are composed inside. A wrapper that merely covers the whole frame is not one: an
 * element placed at a coordinate within it is placed against the frame, by another name. Missing
 * that distinction silenced this rule on every composition that opens with the common
 * `#stage { position: absolute; inset: 0 }` wrapper, which is to say nearly all of them.
 */
function isStage(element: Element, declarations: string): boolean {
    return (
        POSITIONED.test(positionOf(element, declarations)) && !fillsTheFrame(element, declarations)
    );
}

function ownRulesOf(html: string): { selector: string; body: string }[] {
    const rules: { selector: string; body: string }[] = [];
    styleBlocks(html).forEach((css) => {
        let root;
        try {
            root = postcss.parse(css);
        } catch {
            return;
        }
        root.walkRules((rule) => {
            const body = rule.nodes
                .map((node) => (node.type === 'decl' ? `${node.prop}: ${node.value}` : ''))
                .filter(Boolean)
                .join('; ');
            rule.selector.split(',').forEach((selector) => {
                rules.push({ selector: selector.trim(), body });
            });
        });
    });
    return rules;
}

const unmarkedAbsoluteOffsets: LintRule = {
    id: 'unmarked-absolute-offsets',
    severity: 'error',
    guidance:
        'Do not place content with literal coordinates. An absolutely-positioned element with an ' +
        'authored `left`/`top`/`right`/`bottom` is a number chosen for that one element, so ' +
        'nothing in the frame shares an edge by construction and blocks land on unrelated ' +
        'origins. Put content in a frame-filling layout root instead — ' +
        '`class="absolute inset-0 flex items-center justify-center p-24"` and its grid/stack ' +
        'variants — and let flex and grid place it, so every block measures from the same frame ' +
        'margin. A decorative element genuinely placed at a point (a backdrop shape, a graphic ' +
        'anchored off one edge) keeps its coordinates and declares them with `data-l5-absolute`; ' +
        'copy never does.',
    check(html: string): string[] {
        if (typeof DOMParser === 'undefined') {
            return [];
        }
        const document = new DOMParser().parseFromString(html, 'text/html');
        const ownRules = ownRulesOf(html);
        const margin = tokenPx(parseDesignTokens(styleBlocks(html).join('\n')), '--ds-margin');
        const onTheMargin = (offset: string): boolean => {
            const px = /:\s*(\d*\.?\d+)px$/.exec(offset)?.[1];
            return margin !== null && px !== undefined && Number(px) === margin;
        };
        const messages: string[] = [];

        document.querySelectorAll('*').forEach((element) => {
            if (element.hasAttribute('data-l5-absolute')) {
                return;
            }
            const declarations = declarationsFor(element, ownRules);
            if (!isAbsolutelyPositioned(positionOf(element, declarations))) {
                return;
            }
            // An element inside a stage is placed against that stage, not against the frame — a
            // diagram's parts, a label on a card. That is composition, not a coordinate picked for
            // the frame, and it is the overwhelming majority of absolute positioning in a graphic.
            // Only what resolves against the frame itself is judged.
            const insideAStage = [...ancestorsOf(element)].some((ancestor) =>
                isStage(ancestor, declarationsFor(ancestor, ownRules)),
            );
            if (insideAStage) {
                return;
            }
            const offsets = nonZeroOffsets(declarations);
            if (!offsets.length || offsets.every(onTheMargin)) {
                return;
            }
            const name = element.id ? `#${element.id}` : `<${element.tagName.toLowerCase()}>`;
            messages.push(
                `${name} is placed against the frame at a literal coordinate ` +
                    `(\`${offsets.join('; ')}\`), so it shares an edge with nothing else in the ` +
                    'frame. Put it in a layout root and let flex or grid place it, or mark it ' +
                    '`data-l5-absolute` if the point is genuinely deliberate.',
            );
        });

        return messages;
    },
};

export const LAYOUT_LINT_RULES: LintRule[] = [unknownLayoutClass, unmarkedAbsoluteOffsets];
